import { useState } from "react";
import { useNotes } from "@/hooks/useLearning";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";

const subjectColors: Record<string, string> = {
  Matematik: "bg-primary-100 text-primary-700",
  Türkçe: "bg-accent-100 text-accent-700",
  "Fen Bilimleri": "bg-secondary-100 text-secondary-800",
  İngilizce: "bg-red-50 text-red-600",
  "Sosyal Bilgiler": "bg-accent-100 text-accent-700",
};

export default function Notlarim() {
  const { data, loading, error, reload, addNote, deleteNote } = useNotes();
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("Matematik");
  const [content, setContent] = useState("");

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  const items = data ?? [];

  const handleAdd = () => {
    if (!title.trim() || !content.trim()) return;
    addNote(title.trim(), subject, content.trim());
    setTitle("");
    setContent("");
    setShowForm(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">Notlarım</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            Ders çalışırken aldığın kişisel notlar.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" aria-hidden="true"></i>
          Yeni Not
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((note) => (
          <div key={note.id} className="bg-background-50 rounded-xl border border-background-200 p-5 flex flex-col">
            <div className="flex items-start justify-between gap-2">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${subjectColors[note.subject] ?? "bg-background-100 text-foreground-600"}`}>
                {note.subject}
              </span>
              <button
                type="button"
                onClick={() => deleteNote(note.id)}
                className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <i className="ri-delete-bin-line" aria-hidden="true"></i>
              </button>
            </div>
            <h3 className="mt-3 font-heading font-semibold text-base text-foreground-950">{note.title}</h3>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed flex-1">{note.content}</p>
            <p className="mt-4 text-xs text-foreground-400">{note.date}</p>
          </div>
        ))}
      </div>

      {showForm && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setShowForm(false)}
        >
          <div
            className="bg-background-50 rounded-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg text-foreground-950">Yeni Not</h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition-colors"
              >
                <i className="ri-close-line text-lg" aria-hidden="true"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Başlık</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Not başlığı"
                  className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ders</label>
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer"
                >
                  {["Matematik", "Türkçe", "Fen Bilimleri", "Sosyal Bilgiler", "İngilizce"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Not</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  maxLength={500}
                  placeholder="Notunu yaz..."
                  className="w-full h-28 px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 resize-none"
                />
              </div>
              <button
                type="button"
                onClick={handleAdd}
                className="w-full h-10 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}